/**
 * Chummys hero switcher — <chummys-hero>
 * ---------------------------------------
 * - Icon switcher (click / keyboard / swipe / optional autoplay) crossfades one
 *   banner per treat and slides the highlight disc behind the active icon.
 * - Deferred banner images hydrate on hover, on switch and when the browser is
 *   idle, so the homepage only pays for the first banner up front.
 * - Optional per-banner video loads on demand and pauses when hidden.
 * - Scroll progress is written to <html> as --ch-progress / --ch-p2 and the
 *   `chummys-scrolled` class (header logo swap) — the visuals live in CSS.
 */
(() => {
  if (customElements.get('chummys-hero')) return;

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const mqDesktop = window.matchMedia('(min-width: 768px)');
  const mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const root = document.documentElement;
  const idle = window.requestIdleCallback || ((fn) => window.setTimeout(fn, 1200));

  class ChummysHero extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.scrolled = false;
      this.hydrated = new Set();
      this.autoplayTimer = null;
      this.labelTimer = null;
      this.raf = null;
      this.paused = false;
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
    }

    connectedCallback() {
      this.sticky = this.querySelector('.chummys-hero__sticky');
      this.frame = this.querySelector('.chummys-hero__frame');
      this.panels = Array.from(this.querySelectorAll('.chummys-hero__panel'));
      this.tabs = Array.from(this.querySelectorAll('.chummys-hero__tab'));
      this.switcher = this.querySelector('.chummys-hero__switch');
      this.highlight = this.querySelector('.chummys-hero__highlight');
      this.label = this.querySelector('.chummys-hero__switch-label');
      if (!this.panels.length) return;

      root.classList.add('chummys-has-hero');

      // connectedCallback runs again whenever the element is moved in the DOM;
      // element-internal listeners must not stack up.
      const firstConnect = !this.bound;
      this.bound = true;

      this.index = clamp(parseInt(this.dataset.default || '0', 10) || 0, 0, this.panels.length - 1);
      this.hydrated.add(this.index);

      if (firstConnect) this.tabs.forEach((tab) => {
        const i = Number(tab.dataset.index);
        tab.addEventListener('click', () => this.activate(i, { user: true }));
        tab.addEventListener('mouseenter', () => { this.hydrate(i); this.showLabel(i, true); });
        tab.addEventListener('mouseleave', () => this.showLabel(this.index, false));
        tab.addEventListener('focus', () => this.showLabel(i, true));
        tab.addEventListener('blur', () => this.showLabel(this.index, false));
      });
      if (this.switcher && firstConnect) {
        this.switcher.addEventListener('keydown', (e) => this.onKey(e));
        this.switcher.addEventListener('mouseenter', () => { this.paused = true; });
        this.switcher.addEventListener('mouseleave', () => { this.paused = false; });
      }

      if (firstConnect) {
        this.bindSwipe();
        this.bindDesignMode();
      }

      this.setActiveState(this.index);
      this.positionHighlight(true);
      this.playVideo(this.index);

      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('scroll', this.onScroll, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('chummys:intro:done', this.onScroll);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.positionHighlight(true)).catch(() => {});
      }

      // Warm the other banners once the page is idle — at the smallest srcset
      // step, so a visitor who never switches tabs does not pay for six
      // full-resolution hero images.
      const preload = () => idle(() => this.panels.forEach((_, i) => this.hydrate(i, { cheap: true })));
      if (document.readyState === 'complete') preload();
      else window.addEventListener('load', preload, { once: true });

      this.placeProof();
      window.addEventListener('load', () => this.placeProof(), { once: true });
      this.update();
      this.startAutoplay();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('chummys:intro:done', this.onScroll);
      this.stopAutoplay();
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null; // a stale id would block every future scroll update
      root.classList.remove('chummys-has-hero', 'chummys-scrolled');
      root.style.removeProperty('--ch-progress');
      root.style.removeProperty('--ch-p2');
    }

    /* ------------------------------------------------------------ */
    /* Switching                                                     */
    /* ------------------------------------------------------------ */
    activate(i, opts = {}) {
      if (!this.panels[i]) return;
      if (i === this.index) {
        if (opts.user) this.showLabel(i, true, 1200);
        return;
      }
      const prev = this.index;
      this.index = i;
      this.hydrate(i);
      this.setActiveState(i);
      this.positionHighlight();
      this.showLabel(i, true, 1400);
      this.playVideo(i);
      this.pauseVideo(prev);
      if (opts.focus) this.tabs[i] && this.tabs[i].focus({ preventScroll: true });
      if (opts.user) this.startAutoplay();
      this.dispatchEvent(new CustomEvent('chummys:hero:change', {
        bubbles: true,
        detail: { index: i, label: this.tabs[i] ? this.tabs[i].dataset.label : '' }
      }));
    }

    next(step = 1, opts = {}) {
      const n = this.panels.length;
      this.activate((this.index + step + n) % n, opts);
    }

    setActiveState(i) {
      this.panels.forEach((panel, k) => {
        const on = k === i;
        panel.classList.toggle('is-active', on);
        panel.setAttribute('aria-hidden', on ? 'false' : 'true');
        if ('inert' in panel) panel.inert = !on;
      });
      this.tabs.forEach((tab, k) => {
        const on = k === i;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });
    }

    positionHighlight(immediate = false) {
      const tab = this.tabs[this.index];
      if (!tab || !this.highlight || !this.switcher) return;
      const first = this.tabs[0];
      const x = tab.offsetLeft - first.offsetLeft;
      if (immediate) this.highlight.style.transition = 'none';
      this.highlight.style.width = `${tab.offsetWidth}px`;
      this.highlight.style.height = `${tab.offsetHeight}px`;
      this.highlight.style.transform = `translateX(${x}px)`;
      this.switcher.style.setProperty('--ch-label-x', `${tab.offsetLeft + tab.offsetWidth / 2}px`);
      if (immediate) {
        void this.highlight.offsetHeight; // flush so the next move animates
        this.highlight.style.transition = '';
      }
    }

    showLabel(i, show, autoHide = 0) {
      if (!this.label || !this.switcher) return;
      window.clearTimeout(this.labelTimer);
      const tab = this.tabs[i];
      if (!tab) return;
      this.label.textContent = tab.dataset.label || '';
      this.switcher.style.setProperty('--ch-label-x', `${tab.offsetLeft + tab.offsetWidth / 2}px`);
      this.label.classList.toggle('is-visible', !!show);
      if (show && autoHide) {
        this.labelTimer = window.setTimeout(() => this.label.classList.remove('is-visible'), autoHide);
      }
    }

    onKey(event) {
      const map = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
      if (event.key in map) {
        event.preventDefault();
        this.next(map[event.key], { user: true, focus: true });
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.activate(0, { user: true, focus: true });
      } else if (event.key === 'End') {
        event.preventDefault();
        this.activate(this.panels.length - 1, { user: true, focus: true });
      }
    }

    bindSwipe() {
      if (!this.frame) return;
      let startX = 0;
      let startY = 0;
      let tracking = false;
      this.frame.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 1) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        tracking = true;
      }, { passive: true });
      this.frame.addEventListener('touchend', (e) => {
        if (!tracking) return;
        tracking = false;
        const t = e.changedTouches[0];
        const dx = t.clientX - startX;
        const dy = t.clientY - startY;
        if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.4) {
          this.next(dx < 0 ? 1 : -1, { user: true });
        }
      }, { passive: true });
    }

    bindDesignMode() {
      if (!window.Shopify || !window.Shopify.designMode) return;
      this.addEventListener('shopify:block:select', (event) => {
        const panel = event.target.closest('.chummys-hero__panel');
        if (panel) this.activate(Number(panel.dataset.index));
      });
    }

    /* ------------------------------------------------------------ */
    /* Media                                                         */
    /* ------------------------------------------------------------ */
    hydrate(i, opts = {}) {
      if (this.hydrated.has(i) || !this.panels[i]) return;
      // a cheap warm-up still counts as un-hydrated, so a later real switch can
      // upgrade the image to the full srcset
      if (!opts.cheap) this.hydrated.add(i);
      this.panels[i].querySelectorAll('[data-srcset], [data-src]').forEach((el) => {
        if (el.tagName === 'VIDEO' || (el.tagName === 'SOURCE' && el.closest('video'))) return;
        if (opts.cheap) {
          if (el.tagName !== 'IMG' || el.getAttribute('src')) return;
          const smallest = (el.dataset.srcset || '').split(',')[0].trim().split(' ')[0];
          if (smallest) el.setAttribute('src', smallest);
          return;
        }
        if (el.dataset.srcset) { el.srcset = el.dataset.srcset; el.removeAttribute('data-srcset'); }
        if (el.dataset.src) { el.src = el.dataset.src; el.removeAttribute('data-src'); }
      });
    }

    playVideo(i) {
      const video = this.panels[i] && this.panels[i].querySelector('video');
      if (!video) return;
      if (mqReduced.matches || (navigator.connection && navigator.connection.saveData)) return;
      if (!video.dataset.ready) {
        video.querySelectorAll('source[data-src]').forEach((s) => { s.src = s.dataset.src; s.removeAttribute('data-src'); });
        video.dataset.ready = '1';
        video.load();
      }
      const p = video.play();
      const mark = () => video.classList.add('is-playing');
      if (p && p.then) p.then(mark).catch(() => {});
      else mark();
    }

    pauseVideo(i) {
      const video = this.panels[i] && this.panels[i].querySelector('video');
      if (!video) return;
      video.pause();
      video.classList.remove('is-playing');
    }

    /* ------------------------------------------------------------ */
    /* Autoplay                                                      */
    /* ------------------------------------------------------------ */
    startAutoplay() {
      this.stopAutoplay();
      const seconds = parseFloat(this.dataset.autoplay || '0');
      if (!seconds || mqReduced.matches || this.panels.length < 2) return;
      this.autoplayTimer = window.setInterval(() => {
        if (document.hidden || this.paused) return;
        if (this.switcher && this.switcher.contains(document.activeElement)) return;
        this.next(1);
      }, seconds * 1000);
    }

    stopAutoplay() {
      if (this.autoplayTimer) window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    onVisibility() {
      if (document.hidden) this.pauseVideo(this.index);
      else this.playVideo(this.index);
    }

    /* ------------------------------------------------------------ */
    /* Scroll effect                                                 */
    /* ------------------------------------------------------------ */
    onScroll() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => { this.raf = null; this.update(); });
    }

    onResize() {
      this.positionHighlight(true);
      this.placeProof();
      this.onScroll();
    }

    /**
     * The proof card straddles the frame's bottom edge and must shrink about
     * the same point the frame does (its centre on desktop, its top edge on
     * mobile) or it drifts off the edge mid-scroll. Both are measured with
     * offset geometry, which ignores the transforms in flight, relative to the
     * sticky they share; the CSS reads the result as the card's transform
     * origin.
     */
    placeProof() {
      const proof = this.querySelector('.chummys-hero__proof');
      if (!proof || !this.frame) return;
      const originY = this.frame.offsetTop + this.frame.offsetHeight * (mqDesktop.matches ? 0.5 : 0);
      proof.style.setProperty('--chp-oy', `${(originY - proof.offsetTop).toFixed(1)}px`);
    }

    update() {
      if (root.classList.contains('chummys-intro')) {
        root.style.setProperty('--ch-progress', '0');
        root.style.setProperty('--ch-p2', '0');
        return;
      }
      const y = window.scrollY || root.scrollTop || 0;
      const pinned = mqDesktop.matches && !this.hasAttribute('data-no-pin') && this.sticky;
      let progress = 0;

      if (mqReduced.matches) {
        root.style.setProperty('--ch-progress', '0');
        root.style.setProperty('--ch-p2', '0');
        this.setScrolled(y > 40, y <= 20);
        return;
      }

      if (pinned) {
        const pin = Math.max(1, this.offsetHeight - this.sticky.offsetHeight);
        progress = clamp(-this.getBoundingClientRect().top / pin, 0, 1);
        // Hand the wordmark to the header only as the hero one finishes
        // fading, so the two are never on screen together.
        this.setScrolled(progress >= 0.86, progress <= 0.78);
      } else {
        progress = clamp(y / 260, 0, 1);
        this.setScrolled(y > 36, y <= 14);
      }

      root.style.setProperty('--ch-progress', progress.toFixed(4));
      root.style.setProperty('--ch-p2', Math.min(1, progress * 2).toFixed(4));
    }

    setScrolled(on, off) {
      if (on && !this.scrolled) {
        this.scrolled = true;
        root.classList.add('chummys-scrolled');
      } else if (off && this.scrolled) {
        this.scrolled = false;
        root.classList.remove('chummys-scrolled');
      }
    }
  }

  customElements.define('chummys-hero', ChummysHero);
})();
